/**
 * @fileoverview User directory query hook.
 * @description Wraps getAllUsers in a React Query query, keyed by its filters. Keeps the previous
 * result on screen while a changed filter loads, so filtering doesn't flash the loading skeleton.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query'

import { USERS_QUERY_KEYS } from '@/features/users/constants/users-query-keys.constants'
import { getAllUsers } from '@/features/users/services/users.service'

import type { DirectoryUser, ListUsersFilters } from '@/features/users/types/users.types'
import type { ApiError } from '@/shared/types/http.types'

/**
 * @description Fetches every user the caller may see that matches the filters.
 * @param {ListUsersFilters} [filters] - Filters and sort.
 * @returns {import('@tanstack/react-query').UseQueryResult<DirectoryUser[], ApiError>} The query
 * result.
 */
export function useUsers(filters: ListUsersFilters = {}) {
  return useQuery<DirectoryUser[], ApiError>({
    queryKey: [...USERS_QUERY_KEYS.directory, filters],
    queryFn: () => getAllUsers(filters),
    placeholderData: keepPreviousData,
  })
}
